import { useRef, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import { Nullable } from '~globals/types';
import { useLazyRequest } from '~hooks/index';
import { exportSettlementPDF } from '~services/billingItem';
import { formatId, formatterDate } from '~utils/formatter';
import { downloadFileByRequest } from '~utils/download';

interface DownloadSettlementPDFarams {
  onSuccess?: () => void;
  onError?: () => void;
}

interface DownloadSettlementPDFReturn {
  loading: boolean;
  onDownloadPDF: (settlementId: string) => Promise<void>;
}

export const useDownloadSettlementPDF = (
  options?: DownloadSettlementPDFarams,
): DownloadSettlementPDFReturn => {
  const currentSettlementId = useRef<Nullable<string>>(null);
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingDownload, , executeDownload] = useLazyRequest({
    request: exportSettlementPDF,
    withPostSuccess: (response) => {
      if (response.headers && response.data) {
        const currentDate = formatterDate(new Date(), { format: 'YYYYMMDD' });

        const defaultFileName = `liquidacion-${formatId(
          currentSettlementId.current ?? '',
        )}-${currentDate}.pdf`;

        options?.onSuccess?.();

        downloadFileByRequest(response.headers, response.data, defaultFileName);

        enqueueSnackbar(`Se ha descargado la liquidación correctamente`, {
          variant: 'success',
        });
      } else {
        enqueueSnackbar(
          'No se pudo descargar la liquidación, intente nuevamente',
          { variant: 'error' },
        );
      }
    },
    withPostFailure: () => {
      options?.onError?.();

      enqueueSnackbar(
        'No se pudo descargar la liquidación, intente nuevamente',
        { variant: 'error' },
      );
    },
  });

  const onDownloadPDF = useCallback(
    async (settlementId: string) => {
      currentSettlementId.current = settlementId;
      await executeDownload(settlementId);
      currentSettlementId.current = null;
    },
    [executeDownload],
  );

  return { loading: loadingDownload, onDownloadPDF };
};
